import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';

const BACKGROUND_COLOR = 'backgroundColor';
const CENTER = 'center';
const HAS_LINK_TEXT = 'hasLinkText';
const HIDE_BLOCK = 'hideBlock';

interface ComplianceCardProps {
  [BACKGROUND_COLOR]: string;
  [CENTER]: boolean;
  [HAS_LINK_TEXT]: boolean;
}

interface CardBlock {
  [HIDE_BLOCK]: boolean;
}

const complianceCardOptions = {
  shouldForwardProp: (propName: string) =>
    ![BACKGROUND_COLOR, CENTER, HAS_LINK_TEXT].includes(propName),
};

const cardBlockOptions = {
  shouldForwardProp: (propName: string) => propName !== HIDE_BLOCK,
};

export const ComplianceCardStyled = styled(
  Grid,
  complianceCardOptions,
) <ComplianceCardProps>`
  padding: 0;
  background-color: ${({ backgroundColor }) => backgroundColor};
  text-align: ${({ center }) => (center ? 'center' : 'left')};

  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: 40px 32px 22px;

    > div {
      gap: 0;
    }
  }

  > div {
    flex-direction: column;
    gap: 32px;

    ${({ theme }) => theme.breakpoints.down('lg')} {
      gap: 16px;
    }
  }
`;

export const ImageBoxStyled = styled(Grid, cardBlockOptions) <CardBlock>`
  display: ${({ hideBlock }) => (hideBlock ? 'none' : 'block')};

  img {
    height: 50px;
  }
`;

export const TitleBoxStyled = styled(Grid, cardBlockOptions) <CardBlock>`
  display: ${({ hideBlock }) => (hideBlock ? 'none' : 'block')};

  > h2,
  h3 {
    font-family: 'Ivar Display';
    font-size: 48px;
    letter-spacing: -2px;
    line-height: 70px;

    ${({ theme }) => theme.breakpoints.down('lg')} {
      font-size: 36px;
      line-height: 68px;
      letter-spacing: -0.7px;
    }

    ${({ theme }) => theme.breakpoints.down('sm')} {
      letter-spacing: -0.72px;
      text-align: left;
      line-height: 36px;
    }
  }
`;

export const MainTextBoxStyled = styled(Grid, cardBlockOptions) <CardBlock>`
  display: ${({ hideBlock }) => (hideBlock ? 'none' : 'block')};
  padding: 0 136px;
  margin-top: 12px;
  text-align: center;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    margin-top: 0;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: 0;
    margin-top: 14px;
    margin-bottom: 0;
  }

  > h4,
  h5 {
    letter-spacing: 0.5px;
    line-height: 28px;

    ${({ theme }) => theme.breakpoints.down('lg')} {
      letter-spacing: 0.4px;
    }

    ${({ theme }) => theme.breakpoints.down(1025)} {
      font-size: 20px;
    }

    ${({ theme }) => theme.breakpoints.down('sm')} {
      font-size: 20px;
      text-align: left;
      letter-spacing: 0.3px;
      line-height: 26px;
    }
  }
`;

export const ButtonBoxStyled = styled(Grid, cardBlockOptions) <CardBlock>`
  display: ${({ hideBlock }) => (hideBlock ? 'none' : 'block')};
  margin-top: 8px;
  > button {
    width: 100%;
    line-height: 24px;
    letter-spacing: 0.4px;
    background: #080a2d;
    color: #ffffff;
  }
`;

export const CarouselBoxStyled = styled(Grid, cardBlockOptions) <CardBlock>`
  display: ${({ hideBlock }) => (hideBlock ? 'none' : 'block')};
  width: auto;

  > div {
    flex-wrap: nowrap;
    gap: 64px;

    ${({ theme }) => theme.breakpoints.down('lg')} {
      gap: 15px;
    }
  }
`;

export const CarouselImageBoxStyled = styled(Grid)`
  display: block;
  margin-bottom: 32px;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    margin-bottom: 16px;
  }

  > div {
    box-shadow: 4px 16px 32px 0 rgba(29, 29, 29, 0.08);
    overflow: clip;
    border-radius: 20px;
    width: 364px;
    height: 383px;

    ${({ theme }) => theme.breakpoints.down('lg')} {
      width: 300px;
      height: 362px;
    }

    > img {
      height: 243px;
    }

    > div {
      display: flex;
      flex-direction: column;
      text-align: left;
      padding: 35px 40px;
      row-gap: 10px;

      > div {
        font-size: 24px;
        line-height: 24px;
        letter-spacing: 0.5px;
      }

      ${({ theme }) => theme.breakpoints.down('lg')} {
        padding: 32px 40px;
        row-gap: 0;

        > div {
          font-size: 20px;
          line-height: 28px;
          letter-spacing: 0.4px;
        }
      }
    }
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    display: none;
  }
`;

export const LowerButtonStyled = styled(Button, cardBlockOptions) <CardBlock>`
    display: ${({ hideBlock }) => (hideBlock ? 'none' : 'block')};
    padding: 16px 30px;
    margin: 0 auto;
    width: fit-content;
    font-size: 16px;
    border-color: #080a2d;
    border-radius: 10px;
    line-height: 22px;
    letter-spacing: 0.8px;
    background-color: #080a2d;
    color: #ffffff;

    ${({ theme }) => theme.breakpoints.down('lg')} {
      padding: 16px 30px;
      width: 336px;
      margin-top: 12px;
    }

    ${({ theme }) => theme.breakpoints.down('sm')} {
      padding: 16px 30px;
      width: 100%;
      margin-top: 16px;
    }
`;
